class TekaRoomCalculator extends HTMLElement {
  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    this.areaInput = this.querySelector('[data-room-area]');
    this.unitQuantityInput = this.querySelector('[data-room-unit-quantity]');
    this.wastageInput = this.querySelector('[data-room-wastage]');
    this.areaTotal = this.querySelector('[data-room-area-total]');
    this.packTotal = this.querySelector('[data-room-pack-total]');
    this.purchasedArea = this.querySelector('[data-room-purchased-area]');
    this.total = this.querySelector('[data-room-total]');
    this.whatsappButton = this.closest('.product-details')?.querySelector('[data-room-quote]');
    this.unitLabel = this.dataset.unitLabel || 'pack';
    this.unitLabelPlural = this.dataset.unitLabelPlural || `${this.unitLabel}s`;
    this.inputMode = null;
    const { signal } = this.abortController;

    this.areaInput?.addEventListener('input', this.handleAreaInput, { signal });
    this.areaInput?.addEventListener('change', this.handleAreaInput, { signal });
    this.unitQuantityInput?.addEventListener('input', this.handleQuantityInput, { signal });
    this.unitQuantityInput?.addEventListener('change', this.handleQuantityInput, { signal });
    this.wastageInput?.addEventListener('input', this.handleWastageInput, { signal });
    this.wastageInput?.addEventListener('change', this.handleWastageInput, { signal });
    this.calculate();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  handleAreaInput = () => {
    this.inputMode = 'area';
    this.calculate();
  };

  handleQuantityInput = () => {
    this.inputMode = 'quantity';
    this.calculate();
  };

  handleWastageInput = () => {
    this.calculate();
  };

  calculate() {
    // Read references on each calculation so the component also works after a section rerender.
    this.areaInput = this.querySelector('[data-room-area]');
    this.unitQuantityInput = this.querySelector('[data-room-unit-quantity]');
    this.wastageInput = this.querySelector('[data-room-wastage]');
    this.areaTotal = this.querySelector('[data-room-area-total]');
    this.packTotal = this.querySelector('[data-room-pack-total]');
    this.purchasedArea = this.querySelector('[data-room-purchased-area]');
    this.total = this.querySelector('[data-room-total]');
    const productDetails = this.closest('.product-details');
    const productForm = productDetails?.querySelector('product-form-component');
    this.quantityInput =
      productForm?.querySelector('input[name="quantity"]') ?? productDetails?.querySelector('input[name="quantity"]');

    const enteredArea = Number(this.areaInput?.value) || 0;
    const wastage = Number(this.wastageInput?.value) || 0;
    const coverage = Number(this.dataset.coverage) || 0;
    const price = Number(this.dataset.priceCents) || 0;
    const wastageMultiplier = 1 + wastage / 100;
    const quantityMode = Boolean(this.unitQuantityInput) && this.inputMode === 'quantity';
    const enteredQuantity = Math.max(0, Math.floor(Number(this.unitQuantityInput?.value) || 0));
    const units = coverage > 0
      ? quantityMode
        ? enteredQuantity
        : Math.ceil(((enteredArea * wastageMultiplier) / coverage) - 1e-9)
      : 0;
    const purchasedArea = units * coverage;
    if (quantityMode && this.areaInput) {
      const equivalentArea = purchasedArea / wastageMultiplier;
      this.areaInput.value = equivalentArea > 0 ? equivalentArea.toFixed(2) : '0.00';
    } else if (this.unitQuantityInput && this.inputMode === 'area') {
      this.unitQuantityInput.value = String(units);
    }
    const displayedArea = quantityMode ? Number(this.areaInput?.value) || 0 : enteredArea;
    const requiredArea = displayedArea * wastageMultiplier;
    const total = units * price / 100;
    const currency = this.dataset.currency || 'GBP';
    const hasInput = quantityMode ? enteredQuantity > 0 : enteredArea > 0;

    this.dataset.units = String(units);
    if (this.quantityInput && this.inputMode && hasInput) {
      this.quantityInput.value = String(units);
      this.quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
      this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      this.quantityInput.closest('quantity-selector-component')?.updateButtonStates?.();
    } else if (this.quantityInput && this.inputMode && !hasInput) {
      this.quantityInput.value = this.quantityInput.min || '1';
      this.quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
      this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      this.quantityInput.closest('quantity-selector-component')?.updateButtonStates?.();
    }
    this.areaTotal.textContent = hasInput ? `${requiredArea.toFixed(2)} m²` : '— m²';
    this.packTotal.textContent = hasInput ? `${units} ${units === 1 ? this.unitLabel : this.unitLabelPlural}` : '—';
    this.purchasedArea.textContent = hasInput ? `${purchasedArea.toFixed(2)} m²` : '— m²';
    this.total.textContent = hasInput
      ? new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(total)
      : '—';
    if (this.whatsappButton) {
      const message = hasInput
        ? `Hi TEKA, best price on ${this.dataset.productTitle} please — ${quantityMode ? `${units} ${units === 1 ? this.unitLabel : this.unitLabelPlural} (${purchasedArea.toFixed(2)} m²)` : `${displayedArea.toFixed(1)} m²${wastage ? ' plus 10% wastage' : ''}, ${units} ${units === 1 ? this.unitLabel : this.unitLabelPlural}`}.`
        : `Hi TEKA, best price on ${this.dataset.productTitle} please.`;
      this.whatsappButton.href = `${this.dataset.whatsappEndpoint}?text=${encodeURIComponent(message)}`;
    }

    const basketButton = this.closest('.product-details')?.querySelector('.add-to-cart-button');
    if (basketButton && !basketButton.disabled) {
      const textNode = [...basketButton.childNodes].find(
        (node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim(),
      );
      if (textNode && this.inputMode) {
        textNode.textContent = hasInput
          ? `Add ${units} ${units === 1 ? this.unitLabel : this.unitLabelPlural} to basket`
          : 'Add to basket';
      }
    }
  }
}

if (!customElements.get('teka-room-order-calculator')) {
  customElements.define('teka-room-order-calculator', TekaRoomCalculator);
}
